'use client';

import { useRef, useEffect, useCallback, useState } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWallet } from '@/hooks/use-wallet';
import { useChatStream } from '@/hooks/use-chat-stream';
import { useChatDraft } from '@/contexts/chat-draft-context';
import { Loader2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChatMessageActionsProvider, type ChatMessageData } from './chat-message';
import { ChatMessageList } from './chat-message-list';
import { ChatInput, type ReplyPreview } from './chat-input';
import { ChatGateBanner } from './chat-gate-banner';
import { TypingIndicator } from './typing-indicator';
import { classifyVerifyResponse, type VerifyResult } from './chat-verify-response';
import { MiddlewareRateLimitError } from '@/lib/middleware-rate-limit';
import { useRateLimit } from '@/providers/rate-limit-provider';
import { markChatRoomSeen } from '@/hooks/use-boards-chat-unread';
import { markChatSeen } from '@/hooks/use-chat-unread';

interface ChatPanelProps {
  className?: string;
  room?: string;
  /** Deep-link target. When set, the panel scrolls to + briefly
   *  highlights the matching `chat_messages.id` after the first batch
   *  of history hydrates. If the message isn't in the loaded window,
   *  pages of older history are fetched until it is found or `hasNextPage`
   *  is exhausted, at which point we give up and signal `onMessageFocusConsumed`. */
  messageFocusId?: number | null;
  /** Fires once a focus request has been honored, missed, or aborted
   *  (e.g. the room changed). Lets parents drop their pending id so a
   *  later navigation to the same id triggers a fresh scroll. */
  onMessageFocusConsumed?: () => void;
}

export function ChatPanel({
  className,
  room = 'boardroom',
  messageFocusId = null,
  onMessageFocusConsumed,
}: ChatPanelProps) {
  const {
    address,
    isConnected,
    connectWallet,
    getAccessToken,
    signInActiveWallet,
    walletsReady,
    isAddressLinked,
  } = useWallet();
  const queryClient = useQueryClient();
  const { isRateLimited } = useRateLimit();
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  // Tracks whether `isAtBottom` is currently true so a ResizeObserver
  // callback (no React closure) can read the live value without
  // re-creating the observer on every state change.
  const isAtBottomRef = useRef(true);
  // True when the viewer has scrolled MORE than one full chat viewport
  // away from the bottom — i.e. they've intentionally moved into older
  // history rather than just nudging up by a few pixels. We use this
  // (not the much tighter `!isAtBottom` <60px threshold) to gate the
  // "Jump to latest" pill so it doesn't appear for people who are
  // basically still at the bottom and would find the pill noisy.
  const [farFromBottom, setFarFromBottom] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  // Reply target lives in the chat-draft context so an in-flight reply
  // composition survives the panel unmounting (sheet close, mobile
  // chip-rail flip, route swap). Without this, hitting "Reply" on a
  // message and then closing the panel before sending lost the reply
  // target and the user had to re-find the message.
  const { draft, setDraft } = useChatDraft(room);
  const replyTo = draft.replyTo;
  const setReplyTo = useCallback(
    (next: ReplyPreview | null) => setDraft({ replyTo: next }),
    [setDraft],
  );

  // Verify seat holder status on panel open. Keyed on `room` so a board
  // switch re-runs the holder check against the new room's gate, and
  // on `address` so a wagmi wallet switch refetches against the new
  // active wallet.
  //
  // We gate the query on `isAddressLinked` so a wagmi-active wallet
  // that isn't part of the Privy session never round-trips the server
  // for a guaranteed 403 — the SessionMismatch render branch below
  // surfaces "Sign in to chat" instantly. This eliminates one caller
  // from the wallet-switch fanout (each /api route reads envio) and
  // removes the brief Verifying-flicker the user would otherwise see
  // before `walletNotLinked` flipped true.
  //
  // The queryFn delegates to `classifyVerifyResponse` (a pure helper
  // in `./chat-verify-response.ts`) which THROWS on 429 / 5xx / other
  // unexpected statuses. Throwing — instead of returning
  // `{ verified: false }` like the previous implementation — is
  // critical: TanStack would otherwise cache the false answer for
  // `staleTime` (60s) and leave the panel stuck on "Seat required"
  // even after the upstream (Envio) recovered. Throws flow into the
  // rate-limit-aware retry policy inherited from `query-provider.tsx`
  // (2 retries with 5s/15s backoff for rate-limit-like errors), and
  // the global `queryCache.onError` fires the "Reconnecting to
  // network" overlay so the user sees what's happening.
  // Lowercased address segment for the chat-verify queryKey. Wagmi
  // can emit the same wallet either checksummed or lowercased
  // depending on which provider re-prompted last; without
  // normalisation, two casings produce two distinct TanStack
  // observers, two GETs, and (during a wallet switch) miss the
  // `cancelQueries({ queryKey: ['chat-verify', address] })` call in
  // `use-wallet.ts` because that one normalises too.
  const addressKey = address?.toLowerCase() ?? null;

  const {
    data: verifyData,
    isLoading: isVerifying,
    error: verifyError,
  } = useQuery<VerifyResult>({
    queryKey: ['chat-verify', addressKey, room],
    queryFn: async (): Promise<VerifyResult> => {
      const token = await getAccessToken();
      const res = await fetch(`/api/chat/verify?room=${encodeURIComponent(room)}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          // Tell the server which of the user's linked wallets is
          // currently active client-side (wagmi). The server's
          // strict-claim check (`getAuthenticatedAddress` in
          // `lib/auth.ts`) only honours this if it's in the Privy
          // linked set; otherwise we'd get the 403 the classifier
          // surfaces as `walletNotLinked: true` (defensive belt-
          // and-suspenders for the SessionMismatch branch).
          ...(address ? { 'X-Viewer-Address': address } : {}),
        },
      });
      return classifyVerifyResponse(res);
    },
    enabled: isConnected && !!address && isAddressLinked,
    staleTime: 60_000,
    // Retry policy is inherited from `query-provider.tsx` defaults so
    // 429 / rate-limit failures get the same 2-retry / 5s+15s backoff
    // every other query in the app uses. We deliberately do NOT set a
    // local `retry` here.
    //
    // Recovery interval: chat-verify has no natural polling cadence, so
    // a `MiddlewareRateLimitError` (which the global retry policy
    // intentionally does NOT retry — see `query-provider.tsx`) would
    // strand the panel on the "Verifying" loader indefinitely. Tick
    // every 30s while the query is in error state so middleware-
    // throttled verifies recover on their own once the per-IP bucket
    // refills, and stop polling once a successful read lands.
    refetchInterval: (query) => (query.state.error ? 30_000 : false),
  });

  // Immediate recovery when the rate-limit overlay clears. The
  // `refetchInterval` above is the safety net (30s tick); this
  // effect short-circuits it so the chat panel re-verifies as
  // soon as the global rate-limit overlay times out, instead of
  // making the user stare at "Verifying" for up to 30s after the
  // throttle has already lifted.
  useEffect(() => {
    if (isRateLimited) return;
    if (!verifyError) return;
    queryClient.invalidateQueries({ queryKey: ['chat-verify', addressKey, room] });
  }, [isRateLimited, verifyError, queryClient, addressKey, room]);

  const isVerified = verifyData?.verified === true;
  const walletNotLinked = verifyData?.walletNotLinked === true;
  // `is_dev` flips the trash button on for any message in this room. The
  // server-side DELETE handler enforces the actual permission via
  // `isDevAddress(...)`; this flag is purely a UI toggle so the button
  // even renders. If the server says false, the client doesn't surface
  // moderator UI even if the viewer is locally on the allow-list.
  const canModerate = verifyData?.is_dev === true;

  // Manual sign-in flow for the wagmi-active wallet.
  //
  // Triggered by the user clicking "Sign in" on the gate banner —
  // never automatically. The user always gets a chance to read the
  // gate copy ("Sign in to chat" + "We need to verify this wallet
  // before checking seat ownership.") before a Privy/wallet popup
  // appears, so the SIWE prompt is never out-of-context.
  //
  // `signInActiveWallet` (in `use-wallet.ts`) handles the Privy
  // session switch transparently: if the wagmi-active wallet is
  // already in the current session it's a no-op; otherwise it logs
  // out from the current Privy session and runs `loginOrLink` on
  // the LOGIN path, which works whether the wallet has its own
  // Privy user or not (no "User already exists for this address"
  // dead-end). We deliberately do NOT surface failure-mode copy
  // here — on any failure we just drop back to the visible gate.
  // The Sign in button is the self-evident retry, and Privy's own
  // popup carries any provider-side message.
  const [linkState, setLinkState] = useState<'idle' | 'linking'>('idle');

  // Reset link state when address changes so a stale "linking"
  // spinner from the previous wallet doesn't bleed into the new
  // wallet's gate render.
  useEffect(() => {
    setLinkState('idle');
  }, [address]);

  const handleSignIn = useCallback(async () => {
    setLinkState('linking');
    // `signInActiveWallet` returns a discriminated result and never
    // throws. We ignore the discriminator on purpose (see comment
    // above). On success, `useLinkAccount.onSuccess` in
    // `use-wallet.ts` invalidates `chat-verify`, and the post-logout
    // queryClient.clear() ensures the verify query refetches against
    // the new Privy session — the panel drops out of the gate branch
    // on the next render.
    await signInActiveWallet();
    setLinkState('idle');
  }, [signInActiveWallet]);

  // SSE stream for real-time updates (replaces polling). `room` scopes
  // the subscription to the active board's gate.
  const { typingUsers } = useChatStream({
    enabled: isVerified,
    getAccessToken,
    ownAddress: address,
    room,
  });

  // Fetch messages only when verified — no refetchInterval, SSE handles updates.
  // The cache key includes `room` so each board gets its own backlog.
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingMessages,
    error: messagesError,
  } = useInfiniteQuery({
    queryKey: ['chat-messages', room],
    queryFn: async ({ pageParam }: { pageParam?: number }) => {
      const params = new URLSearchParams({ limit: '50', room });
      if (pageParam) params.set('cursor', String(pageParam));
      const res = await fetch(`/api/chat?${params}`);
      if (!res.ok) throw new Error('Failed to fetch messages');
      const json = await res.json();
      return json.messages as ChatMessageData[];
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 50) return undefined;
      return lastPage[lastPage.length - 1]?.id;
    },
    initialPageParam: undefined as number | undefined,
    staleTime: 30_000,
    enabled: isVerified,
  });

  const allMessages = (() => {
    if (!data?.pages) return [];
    const seen = new Set<number>();
    const deduped: ChatMessageData[] = [];
    for (const page of data.pages) {
      for (const msg of page) {
        if (!seen.has(msg.id)) {
          seen.add(msg.id);
          deduped.push(msg);
        }
      }
    }
    return deduped.reverse();
  })();

  const latestVisibleMessageId =
    allMessages.length > 0 ? allMessages[allMessages.length - 1].id : 0;

  // Mark this room "seen" only when the panel is actually showing
  // messages — i.e. the viewer has cleared every gate above
  // (`linkState !== 'linking'`, `isConnected`, address linked,
  // not rate-limited, verify resolved, `isVerified === true`).
  //
  // The previous implementation fired mark-seen from each parent
  // (LobbyClient on `visiblePanels.has('chat')`, the mobile
  // `/chat?board=` page on mount, the desktop chat-sheet on `open`)
  // — but those signals only mean "the panel mounted", not "the
  // viewer can read the messages". A user who hit the
  // `sign-in-required` / `seat-required` / `connect` gate would have
  // their red dot cleared while staring at the gate, with no chance
  // to actually see what changed. Owning the mark-seen call inside
  // `ChatPanel` couples it to "verified holder branch is rendering",
  // which is the only state where messages are on screen.
  //
  // We also clear the global boardroom bell badge (`markChatSeen`,
  // keyed without a room suffix) when the panel is showing the
  // boardroom, since `<ChatSheet>` and the bell on every page point
  // at the same boardroom stream.
  //
  // Both `markChatRoomSeen` and `markChatSeen` are idempotent
  // (no-op when the stored last-seen id is already >= the new id),
  // so re-firing on every new message arrival via the SSE stream is
  // safe and is exactly what we want — once verified, every new
  // message that lands in `allMessages` extends `latestVisibleMessageId`
  // and the dot stays cleared.
  useEffect(() => {
    if (!isVerified) return;
    if (!address) return;
    if (latestVisibleMessageId <= 0) return;
    const canonicalRoom = room.toLowerCase();
    markChatRoomSeen(latestVisibleMessageId, address, canonicalRoom);
    if (canonicalRoom === 'boardroom') {
      markChatSeen(latestVisibleMessageId, address);
    }
  }, [isVerified, address, room, latestVisibleMessageId]);

  // Keep the ref mirror of `isAtBottom` in sync so the ResizeObserver
  // below (which we deliberately don't tear down on every state change)
  // can read the latest value.
  useEffect(() => {
    isAtBottomRef.current = isAtBottom;
  }, [isAtBottom]);

  // Initial scroll-to-bottom + auto-stick.
  //
  // The previous implementation used `bottomRef.scrollIntoView()` once
  // when `isLoadingMessages` flipped false, but that fires before
  // avatar `<img>`s decode and reflow row heights — the document grew
  // *after* the scroll, so the chat opened at "some higher place".
  //
  // We now (a) snap to bottom on two consecutive `requestAnimationFrame`s
  // when the panel hydrates (one frame to flush the current paint, one
  // more to catch the post-decode reflow) and (b) install a
  // `ResizeObserver` on the inner content wrapper so any subsequent
  // height growth — older history pages, late SSE messages, sheet open
  // animation finishing, fonts/images decoding — re-snaps the viewport
  // while the user is pinned to the bottom. Once the user scrolls up,
  // `handleScroll` flips `isAtBottom` to false and the observer becomes
  // a no-op for that session, so deep-link focus and read-history
  // workflows are preserved.
  useEffect(() => {
    if (isLoadingMessages || !isVerified) return;
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller) return;

    const snap = () => {
      scroller.scrollTop = scroller.scrollHeight;
    };

    // Two-frame initial snap. First rAF: after the current commit's
    // paint. Second rAF: after the browser has had a chance to decode
    // recently-mounted images and reflow their containing rows. Empirically
    // covers ~95% of the "lands above the bottom" cases on first open.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      snap();
      raf2 = requestAnimationFrame(snap);
    });

    if (!content) {
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    const ro = new ResizeObserver(() => {
      // Only auto-stick while the viewer is pinned at the bottom — once
      // they scroll up to read history we leave them alone.
      if (isAtBottomRef.current) {
        scroller.scrollTop = scroller.scrollHeight;
      }
    });
    ro.observe(content);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
    };
  }, [isLoadingMessages, isVerified]);

  // Smooth-scroll to bottom when a fresh message arrives while pinned.
  // The `ResizeObserver` above already takes care of the "content grew"
  // case with an instant snap; this effect adds the smooth-scroll polish
  // that signals "new message at the bottom" to a viewer who's already
  // there.
  useEffect(() => {
    if (isAtBottom && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages.length, isAtBottom]);

  // Track scroll position
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom < 60;
    setIsAtBottom(atBottom);
    // "Far from bottom" = more than one full viewport above the bottom.
    // Using the live `clientHeight` matches the user-facing definition
    // of "viewport" regardless of whether the panel is the slim sheet
    // (mobile) or the full HUD (desktop).
    setFarFromBottom(distanceFromBottom > el.clientHeight);

    if (el.scrollTop < 100 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  /**
   * Smooth-scroll the messages container to the bottom, with mid-flight
   * interruption.
   *
   * Native `scrollTo({behavior: 'smooth'})` can't be cancelled
   * declaratively. To honour the user's wheel/touchstart/keydown while
   * the animation is running we attach one-shot listeners that snap the
   * scroll to its current position (`behavior: 'auto'`) — that's the
   * idiomatic way to abort a smooth-scroll without jumping the user
   * back to where they came from. Mirrors the affordance the user
   * experiences when scrolling to a replied-to comment via
   * `scrollToMessage` (which they referenced as the desired pattern).
   */
  const handleJumpToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const target = el.scrollHeight - el.clientHeight;
    el.scrollTo({ top: target, behavior: 'smooth' });

    let interrupted = false;
    const cleanup = () => {
      el.removeEventListener('wheel', cancel, { capture: true } as EventListenerOptions);
      el.removeEventListener('touchstart', cancel, { capture: true } as EventListenerOptions);
      el.removeEventListener('keydown', cancel, { capture: true } as EventListenerOptions);
      window.clearTimeout(timeout);
    };
    const cancel = () => {
      if (interrupted) return;
      interrupted = true;
      el.scrollTo({ top: el.scrollTop, behavior: 'auto' });
      cleanup();
    };
    el.addEventListener('wheel', cancel, { capture: true, passive: true });
    el.addEventListener('touchstart', cancel, { capture: true, passive: true });
    el.addEventListener('keydown', cancel, { capture: true });
    // Defensive teardown — the smooth scroll resolves within ~600ms
    // even on slow devices; 1500ms gives the longest mobile easing
    // curves a healthy margin before we drop the listeners.
    const timeout = window.setTimeout(cleanup, 1500);
  }, []);

  // Send message mutation
  const sendMutation = useMutation({
    mutationFn: async ({ message, replyToId }: { message: string; replyToId?: number }) => {
      const token = await getAccessToken();
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message,
          room,
          ...(replyToId ? { reply_to_id: replyToId } : {}),
          // Active-wallet hint — see the chat-verify GET above for the
          // wallet-switch rationale.
          ...(address ? { address } : {}),
        }),
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        if (json.cooldown_until) {
          setCooldownUntil(json.cooldown_until);
        }
        throw new Error(json.error || 'Failed to send message');
      }

      return res.json();
    },
    onSuccess: (data) => {
      const newMsg = data.message as ChatMessageData;
      queryClient.setQueryData<{ pages: ChatMessageData[][]; pageParams: unknown[] }>(
        ['chat-messages', room],
        (old) => {
          if (!old) return old;
          const firstPage = old.pages[0] || [];
          if (firstPage.some((m) => m.id === newMsg.id)) return old;
          return { ...old, pages: [[newMsg, ...firstPage], ...old.pages.slice(1)] };
        }
      );
      setIsAtBottom(true);
    },
  });

  // Edit message mutation
  const editMutation = useMutation({
    mutationFn: async ({ id, message }: { id: number; message: string }) => {
      const token = await getAccessToken();
      const res = await fetch(`/api/chat/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message,
          ...(address ? { address } : {}),
        }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to edit message');
      }
      return res.json() as Promise<{ id: number; message: string; edited_at: number }>;
    },
    onSuccess: ({ id, message, edited_at }) => {
      queryClient.setQueryData<{ pages: ChatMessageData[][]; pageParams: unknown[] }>(
        ['chat-messages', room],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) =>
              page.map((m) => (m.id === id ? { ...m, message, edited_at } : m))
            ),
          };
        }
      );
    },
  });

  // React to message mutation (optimistic)
  type ChatPages = { pages: ChatMessageData[][]; pageParams: unknown[] };
  const reactMutation = useMutation({
    mutationFn: async ({ id, emoji }: { id: number; emoji: string }) => {
      const token = await getAccessToken();
      const res = await fetch(`/api/chat/${id}/reactions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          emoji,
          ...(address ? { address } : {}),
        }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to react');
      }
      return res.json() as Promise<{ message_id: number; emoji: string; count: number; added: boolean }>;
    },
    onMutate: async ({ id, emoji }) => {
      await queryClient.cancelQueries({ queryKey: ['chat-messages', room] });
      const previous = queryClient.getQueryData<ChatPages>(['chat-messages', room]);

      const profile = queryClient.getQueryData<{ displayName?: string | null }>(['user-profile', address?.toLowerCase()]);
      const me = { address: address ?? '', display_name: profile?.displayName ?? null };

      queryClient.setQueryData<ChatPages>(['chat-messages', room], (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) =>
            page.map((m) => {
              if (m.id !== id) return m;
              const reactions = [...(m.reactions ?? [])];
              const idx = reactions.findIndex((r) => r.emoji === emoji);
              if (idx >= 0) {
                const r = reactions[idx];
                const prevReactors = r.reactors ?? [];
                if (r.reacted) {
                  const filtered = prevReactors.filter((u) => u.address.toLowerCase() !== me.address.toLowerCase());
                  if (r.count <= 1) reactions.splice(idx, 1);
                  else reactions[idx] = { ...r, count: r.count - 1, reacted: false, reactors: filtered };
                } else {
                  reactions[idx] = { ...r, count: r.count + 1, reacted: true, reactors: [...prevReactors, me] };
                }
              } else {
                reactions.push({ emoji, count: 1, reacted: true, reactors: [me] });
              }
              return { ...m, reactions };
            })
          ),
        };
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['chat-messages', room], context.previous);
      }
    },
  });

  // Delete message mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = await getAccessToken();
      const res = await fetch(`/api/chat/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          // DELETE typically can't carry a body, so the active-wallet
          // hint rides on `X-Viewer-Address`. Without this a wallet-
          // switched user couldn't delete their own message because the
          // server would compare the message's author against the
          // server-preferred (now stale) wallet.
          ...(address ? { 'X-Viewer-Address': address } : {}),
        },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to delete message');
      }
      return id;
    },
    onSuccess: (id) => {
      queryClient.setQueryData<{ pages: ChatMessageData[][]; pageParams: unknown[] }>(
        ['chat-messages', room],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => page.filter((m) => m.id !== id)),
          };
        }
      );
    },
  });

  const handleSend = useCallback(
    async (message: string, replyToId?: number) => {
      await sendMutation.mutateAsync({ message, replyToId });
      setReplyTo(null);
    },
    [sendMutation]
  );

  const handleEdit = useCallback(
    async (id: number, newText: string) => {
      await editMutation.mutateAsync({ id, message: newText });
    },
    [editMutation]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      await deleteMutation.mutateAsync(id);
    },
    [deleteMutation]
  );

  const handleReact = useCallback(
    async (id: number, emoji: string) => {
      await reactMutation.mutateAsync({ id, emoji });
    },
    [reactMutation]
  );

  const handleReply = useCallback((msg: ChatMessageData) => {
    setReplyTo({
      id: msg.id,
      message: msg.message,
      display_name: msg.display_name,
      address: msg.address,
    });
  }, []);

  const scrollToMessage = useCallback((id: number) => {
    const el = scrollRef.current?.querySelector(`[data-message-id="${id}"]`) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('bg-amber-500/15');
    setTimeout(() => el.classList.remove('bg-amber-500/15'), 1500);
  }, []);

  /**
   * Honor a deep-link focus request (e.g. clicking a chat-mention
   * notification). The message may not be in the initial page of
   * history, so we walk older pages until we either find it or hit the
   * end of the backlog. We bail out with `onMessageFocusConsumed` in
   * either terminal case so the parent can drop the pending id; without
   * that signal a stale id would block subsequent navigations to the
   * same message id from re-firing.
   *
   * `consumedFocusRef` guards against the consume callback firing more
   * than once per requested id while the effect re-runs as new pages
   * stream in.
   */
  const consumedFocusRef = useRef<number | null>(null);
  useEffect(() => {
    if (messageFocusId == null) {
      consumedFocusRef.current = null;
      return;
    }
    if (consumedFocusRef.current === messageFocusId) return;
    if (!isVerified) return;
    if (isLoadingMessages) return;

    const found = allMessages.some((m) => m.id === messageFocusId);
    if (found) {
      // Suppress the "stick to bottom on new messages" effect for the
      // duration of the deep-link scroll — without this, a freshly-
      // arriving SSE message in the same render tick would yank the
      // viewport back to the bottom and cancel the focused scroll.
      setIsAtBottom(false);
      // Wait a frame for the row to mount under the new scroll position
      // before measuring — the message could have just arrived via SSE
      // or via an older-history fetch that triggered this very render.
      const raf = requestAnimationFrame(() => {
        scrollToMessage(messageFocusId);
      });
      consumedFocusRef.current = messageFocusId;
      onMessageFocusConsumed?.();
      return () => cancelAnimationFrame(raf);
    }

    // Not yet in view — pull in older history if there's any left.
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
      return;
    }

    // Out of pages — give up so the parent stops re-passing this id.
    if (!hasNextPage) {
      consumedFocusRef.current = messageFocusId;
      onMessageFocusConsumed?.();
    }
  }, [
    messageFocusId,
    isVerified,
    isLoadingMessages,
    allMessages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    scrollToMessage,
    onMessageFocusConsumed,
  ]);

  // Signal typing to other users
  const handleTyping = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) return;
    fetch('/api/chat/typing', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        display_name: undefined, // server resolves from auth
        room,
        ...(address ? { address } : {}),
      }),
    }).catch(() => {});
  }, [getAccessToken, room, address]);

  // Sign-in in flight: pin the sign-in gate. `signInActiveWallet`
  // briefly drops the user to `authenticated=false` (Privy logout
  // step) before the LOGIN-path SIWE re-authenticates them; without
  // this guard, the panel would flicker through "Connect wallet"
  // mid-flow. The button keeps its loading state via `isSigningIn`.
  if (linkState === 'linking') {
    return (
      <div className={cn('flex flex-col items-center justify-center', className)}>
        <ChatGateBanner
          mode="sign-in-required"
          onConnect={handleSignIn}
          isSigningIn
        />
      </div>
    );
  }

  // Not connected: show "Connect wallet" gate.
  if (!isConnected) {
    return (
      <div className={cn('flex flex-col items-center justify-center', className)}>
        <ChatGateBanner mode="connect" onConnect={connectWallet} />
      </div>
    );
  }

  // SessionMismatch: the wagmi-active wallet isn't part of the
  // current Privy session, so we know the chat-verify call would
  // 403 with `wallet_not_linked`. Render the sign-in gate
  // immediately instead of round-tripping the server (one fewer
  // envio caller in the wallet-switch fanout) and avoid the brief
  // Verifying-flicker we'd otherwise see before the 403 lands.
  //
  // `walletsReady` guards against a false negative during Privy's
  // hydration tick when `useWallets()` hasn't yet surfaced the
  // wagmi-active wallet.
  if (walletsReady && !isAddressLinked) {
    return (
      <div className={cn('flex flex-col items-center justify-center', className)}>
        <ChatGateBanner mode="sign-in-required" onConnect={handleSignIn} />
      </div>
    );
  }

  // Verify in flight — generic loader. Covers both the initial
  // load (`isVerifying`) AND any backoff retry that hasn't yet
  // produced data (`verifyError && !verifyData`); without the
  // second condition, a 429 from envio would flash "Seat required"
  // between the failed attempt and the retry firing.
  //
  // We split out the middleware-rate-limit branch (our own per-IP
  // bucket) so the user sees a truthful "throttled, retrying"
  // message instead of an indefinite "Verifying" spinner. The
  // global retry policy correctly does not retry middleware 429s
  // (see `query-provider.tsx`), so without this branch the panel
  // would otherwise read as silently stuck.
  //
  // Sign-in flight uses its own loading state inside the sign-in
  // gate (button label changes to "Signing in…") so it stays
  // attached to the CTA the user just clicked.
  if (verifyError instanceof MiddlewareRateLimitError && !verifyData) {
    return (
      <div className={cn('flex flex-col items-center justify-center', className)}>
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <p className="text-xs text-muted-foreground mt-2">Rate limited — retrying shortly…</p>
      </div>
    );
  }

  if (isVerifying || (verifyError && !verifyData)) {
    return (
      <div className={cn('flex flex-col items-center justify-center', className)}>
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <p className="text-xs text-muted-foreground mt-2">Verifying seat ownership...</p>
      </div>
    );
  }

  // Defensive belt-and-suspenders: server returned 403
  // `wallet_not_linked` even though `isAddressLinked` was true at
  // query time. Can happen if Privy revoked the link between the
  // query firing and the response landing. Surface the sign-in CTA
  // so a single SIWE recovers the session.
  if (walletNotLinked) {
    return (
      <div className={cn('flex flex-col items-center justify-center', className)}>
        <ChatGateBanner mode="sign-in-required" onConnect={handleSignIn} />
      </div>
    );
  }

  // Server returned a confirmed not-verified read: wallet IS in the
  // Privy session, the seat check ran, and they don't hold one.
  if (!isVerified) {
    return (
      <div className={cn('flex flex-col items-center justify-center', className)}>
        <ChatGateBanner mode="seat-required" />
      </div>
    );
  }

  // Verified holder: show messages + input
  return (
   <ChatMessageActionsProvider>
    <div className={cn('flex flex-col min-h-0 overflow-hidden overscroll-y-contain touch-pan-y', className)}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain [overscroll-behavior-y:contain]"
      >
        <ChatMessageList
          allMessages={allMessages}
          isLoadingMessages={isLoadingMessages}
          hasData={!!data}
          messagesError={messagesError}
          isFetchingNextPage={isFetchingNextPage}
          ownAddressLower={address?.toLowerCase() ?? null}
          canModerate={canModerate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onReact={handleReact}
          onReply={handleReply}
          onScrollToMessage={scrollToMessage}
          contentRef={contentRef}
          bottomRef={bottomRef}
        />
      </div>

      <TypingIndicator users={typingUsers} />

      {/* Full-width "Jump to latest" pill — appears once the user has
          scrolled more than one full chat viewport up. Sits between the
          message list and the input so it never covers a message bubble
          and is always within thumb reach above the composer on mobile.
          Smooth-scroll is interruptible via wheel/touchstart/keydown
          (see `handleJumpToBottom`). */}
      {farFromBottom && (
        <button
          type="button"
          onClick={handleJumpToBottom}
          className="w-full px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] bg-amber-500/15 hover:bg-amber-500/25 border-y border-amber-500/30 text-amber-100 flex items-center justify-center gap-2"
        >
          <ChevronDown className="h-3 w-3" />
          Jump to latest
        </button>
      )}

      <ChatInput
        onSend={handleSend}
        onTyping={handleTyping}
        cooldownUntil={cooldownUntil}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        room={room}
      />
    </div>
   </ChatMessageActionsProvider>
  );
}
